/**
 * The pipeline vocabulary, in its own module.
 *
 * Split out of `Leads.tsx` because `react-refresh/only-export-components` — a
 * warning, and `eslint --max-warnings=0` makes warnings fatal — refuses a module
 * that exports both a component and a constant. The same split appears in
 * `ui/toast-context.ts` and `settings/panels.ts` for the same reason.
 *
 * There is a second reason to want it here, and it outlives the linter: three
 * screens render a lead's stage — the list, the board and the lead itself — and a
 * lead that changed colour depending on which one was looking at it would be a
 * defect nobody could see in a single screenshot.
 */

/** `LEAD_STAGES` in `app/db/models/tenant.py`, mapped onto the atlas's badge tones. */
export const STAGE_TONES: Record<string, string> = {
  found: 'neutral',
  contacted: 'info',
  replied: 'success',
  won: 'brand',
  lost: 'warning',
}

export const STAGE_LABELS: Record<string, string> = {
  found: 'Found',
  contacted: 'Contacted',
  replied: 'Replied',
  won: 'Won',
  lost: 'Lost',
}

/**
 * OUTR-18. What a message that did not arrive is called, on the badge the mockup
 * draws in the stage column.
 *
 * **A delivery outcome, deliberately not a sixth stage.** `outreach.html:365`
 * puts a red *Bounced* badge among Found and Replied; the owner settled on
 * 2026-09-09 that it stays where the data is — a lead whose email bounced is
 * still at whatever stage they were, and moving them to a *Bounced* column would
 * lose that. So the badge sits *beside* the stage badge and the stage keeps
 * saying where they are in the pipeline.
 *
 * Four states rather than `bounced` alone, because the API sends four and they
 * are not the same fact: *Bounced* is the address rejecting the mail, *Marked as
 * spam* is the person rejecting it, *Undelivered* is the provider giving up, and
 * *Send failed* is the send never having happened. A tenant deciding whether to
 * find another address needs to know which.
 */
export const DELIVERY_LABELS: Record<string, string> = {
  bounced: 'Bounced',
  complained: 'Marked as spam',
  undelivered: 'Undelivered',
  failed: 'Send failed',
}

/** The tenant's time zone is the only one this product renders in (CLAUDE.md). */
export const IST = 'Asia/Kolkata'

/** `CONTACT_KINDS` in `app/db/models/tenant.py`, in the tenant's words.
 *
 *  Here rather than in `Leads.tsx` because the lead detail labels the same three
 *  kinds (OUTR-61) and CSS `text-transform: capitalize` was doing it there — which
 *  renders `linkedin` as *Linkedin*, a spelling of somebody else's trademark that
 *  no amount of styling makes right. */
export const CONTACT_LABELS: Record<string, string> = {
  email: 'Email',
  mobile: 'Mobile',
  linkedin: 'LinkedIn',
}

/**
 * Which `.app-chan--*` chip a contact kind wears — OUTR-55.
 *
 * The chips are per *channel* (`app.css:1814-1850`) and this column is per
 * *contact kind*, which is why the map exists rather than an interpolation:
 * `mobile` is an address and WhatsApp is the only channel this product can now
 * reach one on, since SMS and RCS were withdrawn on 2026-09-07 and IVR before
 * them. So a mobile number wears WhatsApp's colour because that is what sending
 * to it would actually be.
 */
export const CHANNEL_CHIP: Record<string, string> = {
  email: 'app-chan--email',
  mobile: 'app-chan--whatsapp',
  linkedin: 'app-chan--linkedin',
}

/** LinkedIn's age units, as `app/outreach/posts.parse_relative_age` normalises
 *  them, mapped to the words a sentence needs. `mo` before `m` is load-bearing
 *  there and irrelevant here only because the token is already parsed. */
const AGE_UNITS: Record<string, string> = {
  s: 'second',
  m: 'minute',
  h: 'hour',
  d: 'day',
  w: 'week',
  mo: 'month',
  y: 'year',
  yr: 'year',
}

/**
 * How old the post was when the search found it, in words — OUTR-24.
 *
 * `posted_ago` is LinkedIn's own recency text after
 * `app/outreach/posts.parse_relative_age` has reduced it to a token: `6h`, `3d`,
 * `1mo`, or `now` for a post less than a minute old. A badge reading *"post was
 * 6h old"* is the scrape's shorthand leaking onto a screen, so the token is
 * spelled out.
 *
 * **An unrecognised token returns `null` rather than itself.** The parser's last
 * branch keeps up to twenty-four characters of whatever the page said when it
 * matched nothing, and that string is LinkedIn's markup rather than a fact about
 * the post — drawing it in a badge would be showing a tenant a scrape artefact
 * and calling it an age.
 */
export function postAge(token: string | null): string | null {
  if (token === null) return null
  const cleaned = token.trim().toLowerCase()
  if (cleaned === '') return null
  if (cleaned === 'now') return 'less than a minute'
  const match = /^(\d+)(s|mo|m|h|d|w|yr|y)$/.exec(cleaned)
  if (match === null) return null
  const count = Number(match[1])
  const unit = AGE_UNITS[match[2] ?? '']
  if (unit === undefined) return null
  return `${String(count)} ${unit}${count === 1 ? '' : 's'}`
}

/**
 * The relevance score, as the two-digit figure `outreach.html#leads` draws.
 *
 * Stored 0-1 (`Numeric(5, 4)`) and shown 0-100, because that is the scale the
 * screen was designed on and a verdict rendered as `0.9200` reads as a
 * probability rather than as a rank. An unscored lead is a dash and never a
 * zero: *"nobody judged this"* and *"judged and found irrelevant"* are different
 * facts, and the second would not be in the list at all.
 */
export function formatScore(score: number | null): string {
  if (score === null) return '—'
  return String(Math.round(score * 100))
}
